import { useState } from "react";
import { ApiError } from "@/api/client";
import { useAllWfh, useDecideWfh, useTeamWfh } from "@/api/queries";
import { useSession } from "@/api/session";
import type { WfhRequest } from "@/api/types";
import { Avatar } from "@/components/ui/avatar";
import { FiledLateBadge, StatusBadge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, ErrorState, SkeletonTable } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/toast";
import { formatDate, formatTimestampDate } from "@/lib/format";
import { WfhWeekCharts } from "@/pages/shared/wfh-charts";

/**
 * Deciding other people's work-from-home requests, plus the charts that say
 * whether the pattern is one worth deciding differently.
 *
 * Manager and HR & Admin share this component because they do the same job with
 * it and only the population differs — a manager sees their own team's
 * employees, HR sees everyone. The scope is enforced server-side either way; the
 * `scope` prop only picks which endpoint to read and how to word the copy, so a
 * mistake here cannot widen anybody's access.
 */
export function WfhApprovalsPanel({ scope }: { scope: "manager" | "admin" }) {
  const isManager = scope === "manager";
  const currentUserId = useSession((state) => state.user?.id);

  const team = useTeamWfh(isManager);
  const org = useAllWfh(!isManager);
  const source = isManager ? team : org;

  const [confirming, setConfirming] = useState<WfhConfirming | null>(null);

  const requests = source.data ?? [];
  // Nobody decides their own request, whatever their role — the backend refuses
  // it, and hiding the buttons keeps the screen from offering something it will
  // then refuse. HR & Admin's own requests live on their My leave page.
  const decidable = requests.filter(
    (request) => request.status === "pending" && request.user_id !== currentUserId,
  );
  const scopeLabel = isManager ? "your team" : "the organisation";

  if (source.error) {
    return (
      <Panel>
        <ErrorState message={source.error.message} onRetry={() => void source.refetch()} />
      </Panel>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <WfhWeekCharts scopeLabel={scopeLabel} />

      <Panel>
        <PanelHeader
          title="Work-from-home requests"
          note={
            isManager
              ? "Only requests from employees on your own team reach this screen."
              : "Every request org-wide. You can decide any of them except your own."
          }
        />
        <Tabs defaultValue="pending">
          <div className="border-b border-line px-4 py-2.5">
            <TabsList>
              <TabsTrigger value="pending">Waiting ({decidable.length})</TabsTrigger>
              <TabsTrigger value="all">All ({requests.length})</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="pending">
            <WfhRequestTable
              requests={decidable}
              loading={source.isLoading}
              showTeam={!isManager}
              currentUserId={currentUserId}
              emptyTitle="Nothing waiting on you"
              emptyBody={`Every work-from-home request from ${scopeLabel} has been actioned.`}
              onDecide={(request, status) => setConfirming({ request, status })}
            />
          </TabsContent>
          <TabsContent value="all">
            <WfhRequestTable
              requests={requests}
              loading={source.isLoading}
              showTeam={!isManager}
              currentUserId={currentUserId}
              emptyTitle="No requests yet"
              emptyBody={`Requests appear here as soon as somebody in ${scopeLabel} asks to work from home.`}
              onDecide={(request, status) => setConfirming({ request, status })}
            />
          </TabsContent>
        </Tabs>
      </Panel>

      {confirming && <WfhDecideDialog confirming={confirming} onClose={() => setConfirming(null)} />}
    </div>
  );
}

export type WfhConfirming = { request: WfhRequest; status: "approved" | "rejected" };

/** The confirm step before a work-from-home decision is recorded. Also used by
 *  HR's leave Requests tab, where WFH sits in the same "Waiting on you" list. */
export function WfhDecideDialog({ confirming, onClose }: { confirming: WfhConfirming; onClose: () => void }) {
  const decide = useDecideWfh();

  async function onDecide() {
    try {
      await decide.mutateAsync({ id: confirming.request.id, status: confirming.status });
      toast.success(
        `Work from home ${confirming.status} for ${confirming.request.employee_name ?? "this person"}.`,
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not record that decision.");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={
            confirming.status === "approved"
              ? "Approve this work from home?"
              : "Decline this work from home?"
          }
          description={`${confirming.request.employee_name ?? "This person"} · ${formatDate(
            confirming.request.start_date,
          )}${
            confirming.request.start_date !== confirming.request.end_date
              ? ` to ${formatDate(confirming.request.end_date)}`
              : ""
          }`}
        />
        <DialogBody>
          <dl className="divide-y divide-line rounded-lg border border-line">
            <div className="flex justify-between gap-4 px-3 py-2">
              <dt className="text-[13px] text-muted">Days</dt>
              <dd className="text-[13px] font-medium text-ink">{confirming.request.days}</dd>
            </div>
            <div className="flex justify-between gap-4 px-3 py-2">
              <dt className="text-[13px] text-muted">Reason</dt>
              <dd className="max-w-[60%] whitespace-pre-wrap break-words text-right text-[13px] text-ink">
                {confirming.request.reason}
              </dd>
            </div>
          </dl>
          <p className="mt-3 text-xs leading-relaxed text-muted">
            {confirming.status === "approved"
              ? "On these days they still check in and check out as usual — the day is recorded as remote rather than onsite. No leave is used either way."
              : "They keep working onsite on these days. No leave is used either way."}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={decide.isPending}>
            Cancel
          </Button>
          <Button
            variant={confirming.status === "approved" ? "primary" : "danger"}
            onClick={onDecide}
            loading={decide.isPending}
          >
            {confirming.status === "approved" ? "Approve" : "Decline"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function WfhRequestTable({
  requests,
  loading,
  showTeam,
  currentUserId,
  emptyTitle,
  emptyBody,
  onDecide,
}: {
  requests: WfhRequest[];
  loading: boolean;
  showTeam: boolean;
  currentUserId: number | undefined;
  emptyTitle: string;
  emptyBody: string;
  onDecide: (request: WfhRequest, status: "approved" | "rejected") => void;
}) {
  if (loading) return <SkeletonTable rows={5} cols={showTeam ? 7 : 6} />;
  if (requests.length === 0)
    return <EmptyState icon="wfh" title={emptyTitle} body={emptyBody} />;

  return (
    <TableWrap>
      <THead>
        <TH>Person</TH>
        {showTeam && <TH>Team</TH>}
        <TH>Dates</TH>
        <TH numeric>Days</TH>
        <TH>Reason</TH>
        <TH>Status</TH>
        <TH>
          <span className="sr-only">Decide</span>
        </TH>
      </THead>
      <TBody>
        {requests.map((request) => (
          <TR key={request.id}>
            <TDPrimary>
              <span className="flex items-center gap-2">
                <Avatar name={request.employee_name} size="sm" />
                <span className="truncate">{request.employee_name ?? "Unnamed"}</span>
              </span>
            </TDPrimary>
            {showTeam && <TD>{request.team_name ?? "Unassigned"}</TD>}
            <TD className="whitespace-nowrap font-mono text-xs">
              {formatDate(request.start_date, "d MMM")}
              {request.start_date !== request.end_date &&
                ` to ${formatDate(request.end_date, "d MMM")}`}
            </TD>
            <TD numeric>{request.days}</TD>
            <TD className="max-w-[28ch] whitespace-pre-wrap break-words">{request.reason}</TD>
            <TD>
              <span className="flex flex-wrap items-center gap-1.5">
                <StatusBadge status={request.status} />
                <FiledLateBadge request={request} />
              </span>
            </TD>
            <TD>
              {request.status === "pending" && request.user_id !== currentUserId ? (
                <span className="flex items-center gap-1.5">
                  <Button variant="primary" size="sm" onClick={() => onDecide(request, "approved")}>
                    Approve
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onDecide(request, "rejected")}>
                    Decline
                  </Button>
                </span>
              ) : request.status === "pending" ? (
                <span className="whitespace-nowrap text-xs text-faint">Your own request</span>
              ) : (
                <span className="whitespace-nowrap text-xs text-faint">
                  {request.decided_at ? formatTimestampDate(request.decided_at, "d MMM") : "—"}
                </span>
              )}
            </TD>
          </TR>
        ))}
      </TBody>
    </TableWrap>
  );
}
